import Accordion from '@/common/components/Accordion';
import { AvailableTokensAccordion } from '@/common/components/AvailableTokensAccordion'
import HighlightTextArea from '@/common/components/HighlightTextArea';
import SelectController from '@/common/components/SelectController';
import { fiscalDocumentKindOptions } from '@/features/privateCompanies/constants/fiscalDocumentKind.constant'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview';
import { ReviewButtons } from '@/features/establishmentLeadsFunnel/components/ReviewButtons';
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema';
import { FiscalDocumentInformationData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead';
import { UseMutateFunction } from '@tanstack/react-query';
import { BaseSyntheticEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useBillingBillsDescriptionTokens } from '@/common/hooks/queries/useBillingBillsDescriptionTokens';
import { BillingBillsDescriptionTokensTransformer } from '@/features/privateCompanies/services/billingBillsDescriptionTokensTransformer';
import { Mapper } from '@/common/services/mapper';

interface FiscalDocumentInformationSectionProps {
  fiscal_document_information?: FiscalDocumentInformationData;
  submittableOnBlur?: (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => void;
  submit?: (e?: BaseSyntheticEvent) => Promise<void>;
  approveFiscalDocumentInformationLead?: UseMutateFunction<void, Error, { description?: string; } | undefined, unknown>;
  declineFiscalDocumentInformationLead?: UseMutateFunction<void, Error, { description?: string; } | undefined, unknown>;
  disabled?: boolean;
}

export const FiscalDocumentInformationSection = ({
  fiscal_document_information,
  submittableOnBlur,
  submit,
  approveFiscalDocumentInformationLead,
  declineFiscalDocumentInformationLead,
  disabled,
}: FiscalDocumentInformationSectionProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelUpdateValidationSchema>();

  const { data: descriptionTokens, dataUpdatedAt } = useBillingBillsDescriptionTokens({
    mapper: Mapper.mapWithTransformer({ transformer: BillingBillsDescriptionTokensTransformer.suggestionsTransformer() })
  });

  return (
    <Accordion
      className="py-1"
      defaultOpen
      head={
        <div className="flex w-full items-center justify-between mr-4">
          <p>Detalhes Fiscais <span className="text-danger-soft"> *</span></p>

          {approveFiscalDocumentInformationLead && declineFiscalDocumentInformationLead && (
            <div className="ml-auto">
              <ReviewButtons
                reviewState={fiscal_document_information?.last_review?.state}
                onApprove={approveFiscalDocumentInformationLead}
                onDecline={declineFiscalDocumentInformationLead}
              />
            </div>
          )}
        </div>
      }
      body={
        <div className="flex flex-col gap-4">
          {fiscal_document_information?.last_review?.state === 'disapproved' && (
            <RequestedReview description={fiscal_document_information?.last_review?.description} />
          )}

          <SelectController
            control={control}
            name="fiscal_document_information.fiscal_document_kind"
            label="Tipo de nota fiscal de consumo"
            triggerOnChange={submit}
            isDisabled={disabled}
            options={fiscalDocumentKindOptions}
            defaultValue={fiscalDocumentKindOptions.find(({ value }) => value === fiscal_document_information?.fiscal_document_kind)}
            errorMessage={errors?.fiscal_document_information?.fiscal_document_kind?.message}
          />

          <Controller
            control={control}
            name="fiscal_document_information.consumption_observations"
            defaultValue={fiscal_document_information?.consumption_observations}
            disabled={disabled}
            render={({ field: { ref, onBlur, value, ...rest } }) => (
              <HighlightTextArea
                label="NF de Consumo"
                rows={16}
                maxLength={1024}
                {...rest}
                value={value as string | undefined}
                errorMessage={errors?.fiscal_document_information?.consumption_observations?.message}
                onBlur={(e) => submittableOnBlur?.(e as unknown as React.FocusEvent<HTMLInputElement, Element>, onBlur)}
                disabled={disabled}
                suggestions={descriptionTokens}
              />
            )}
          />

          <Controller
            control={control}
            name="fiscal_document_information.service_observations"
            defaultValue={fiscal_document_information?.service_observations}
            disabled={disabled}
            render={({ field: { ref, onBlur, value, ...rest } }) => (
              <HighlightTextArea
                label="NF de Tarifas"
                rows={16}
                maxLength={1024}
                {...rest}
                value={value as string | undefined}
                errorMessage={errors?.fiscal_document_information?.service_observations?.message}
                onBlur={(e) => submittableOnBlur?.(e as unknown as React.FocusEvent<HTMLInputElement, Element>, onBlur)}
                disabled={disabled}
                suggestions={descriptionTokens}
              />
            )}
          />

          <AvailableTokensAccordion descriptionTokens={descriptionTokens} updatedAt={dataUpdatedAt} />
        </div>
      }
    />
  );
};
